import React, { useContext } from 'react';
import { useTranslation } from '@lib/useTypedTranslation';
import moment, { Moment } from 'moment';
import * as am4charts from '@amcharts/amcharts4/charts';
import { get, groupBy } from 'lodash';

import { IDeviceDataUsage } from '../../../../../../services/core/devicePerformance';
import { IDeviceEventFilterState, deviceEventFilterGroupNames } from '../events/deviceEventsTypes';
import { DeviceEvent } from '../../../../../../services/core/eventsTypes';
import { getDeviceEventFilterGroups } from '../events/deviceEvents';
import { EventsShown } from '../events-shown';
import { DeviceInfoContext } from '../../../index';
import { chartNames, ChartName } from './lib/chartNames';
import { useFeatureTogglesContext } from '../../../../../../context/featureToggles';
import { drawYAxis } from '../../../../../../lib/dataUsageChart';

import './charts.css';
import '../events/deviceEvents.css';
import { formatTooltipDay } from '@components/chart/tooltips';

import { drawXAxisLabelsFromDateRange } from '@components/chart/lib';
import { StackedColumnsDateChart, IProps as IChartProps } from '@components/chart/stackedColumnsDateChart';
import { EditEvents, EditEventsAction, IEventFilterGroups, useEditEventsDispatch } from '../events/edit-events';

export interface IProps {
  data: IDeviceDataUsage[],
  events: DeviceEvent[],
  eventFilters: IDeviceEventFilterState,
  dispatchEventFilters: React.Dispatch<EditEventsAction<ChartName>>,
  setDate: (date: string | Moment) => void,
  onMount: (chart: am4charts.XYChart) => am4charts.XYChart,
  dateRange: { from: number, to: number }
}

export const useChartData = (props: IProps): { eventFilterGroups: IEventFilterGroups, chartProps: Pick<IChartProps<any>, "title" | "data" | "series" | "onBarClick" | "drawXAxis" | "drawYAxis"> } => {
  const { t } = useTranslation('performance');

  const featureToggles = useFeatureTogglesContext();

  const { platformType } = useContext(DeviceInfoContext);

  const eventFilterGroups = getDeviceEventFilterGroups(platformType, featureToggles);

  const data = React.useMemo(() => {
    if (!props.data.length) {
      return [];
    }
    const eventsByDay = groupBy(props.events, (event) => moment.utc(event.local).startOf('day').valueOf());
    return props.data.map(({ date, wifi, mobile }) => {
      return {
        date: moment.utc(parseInt(date)).valueOf(),
        wifi,
        mobile,
        events: get(eventsByDay, date, [])
      };
    });
  }, [props.data, props.events]);

  const series = React.useMemo(() => [
    {
      colour: '#49a1a9',
      dataKey: 'mobile',
      description: t('MOBILE_DATA_LINES'),
      visible: true
    },
    {
      colour: '#2c678c',
      dataKey: 'wifi',
      description: t('WIFI_DATA_LINES'),
      visible: true
    }
  ], [t]);

  const onBarClick = (event: IDeviceDataUsage) => {
    props.setDate(moment.utc(event.date));
  };

  const title = t('DEVICE_DATA_USAGE_PAST_30DAYS');

  return {
    eventFilterGroups,
    chartProps: {
      data,
      series,
      title,
      onBarClick,
      drawYAxis: (chart, axis, enabledSeries) => drawYAxis(chart.data, axis, enabledSeries),
      drawXAxis: (_, xAxis) => drawXAxisLabelsFromDateRange(xAxis as am4charts.DateAxis, props.dateRange)
    }
  };
};

export function DataUsage30Days(props: IProps) {
  const { eventFilters, dispatchEventFilters, onMount } = props;
  const { eventFilterGroups, chartProps } = useChartData(props);
  const ns = 'translation';
  const { t } = useTranslation(['performance', ns]);

  const {
    handleUpdate, handleApply, handleApplyAll, handleCancel
  } = useEditEventsDispatch(
    dispatchEventFilters,
    chartNames.dataUsage30Days,
    /* istanbul ignore next */
    () => {}
  );

  return (
    <div className={`core-device_performance-30-day-data-usage`}>
      <StackedColumnsDateChart<any>
        {...chartProps}
        headerControls={<div className="chart-edit-events">
          <EventsShown eventFilters={eventFilters.appliedEventFilters} />
          <EditEvents
            subheader={`‘${chartProps.title}’`}
            eventGroupNames={deviceEventFilterGroupNames}
            eventFilterGroups={eventFilterGroups}
            eventFilterState={eventFilters.currentEventFilters}
            handleUpdate={handleUpdate}
            handleApply={handleApply}
            handleCancel={handleCancel}
            handleApplyAll={handleApplyAll}
          />
        </div>}
        cypressId="last30DaysDataUsage"
        hasLegend={true}
        yAxisType='value'
        amchartsYAxisNumberFormat="#.00b"
        xAxisType={'date'}
        useAxisTooltip
        totalDescription={t('TOTAL_DATA', { ns: 'translation' })}
        dateAxisEndDuration={{ amount: "1", unit: 'day' }}
        formatTooltipDate={formatTooltipDay}
        eventOverlay={true}
        onMount={onMount}
        adjustChartDimensions={{ height: 250 }}
      />
    </div>
  );
}
